import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import type { User, Admin } from "@/lib/supabase";

export interface AuthUser extends User {
  type: "user";
}

export interface AuthAdmin extends Admin {
  type: "admin";
  sessionToken: string;
  name?: string;
}

type AuthContextType = {
  user: AuthUser | AuthAdmin | null;
  isAuthenticated: boolean;
  login: (user: AuthUser | AuthAdmin) => void;
  logout: () => void;
  isLoading: boolean;
  adminSessionToken: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

interface StoredAuthSession {
  id: string;
  type: "user" | "admin";
  sessionToken?: string;
}

const SECRET_KEY = import.meta.env.VITE_SECRET_KEY || "";

const encrypt = (data: string) => {
  try {
    if (!SECRET_KEY) {
      return btoa(encodeURIComponent(data));
    }
    return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
  } catch (err) {
    console.error("Encrypt error:", err);
    try {
      return btoa(encodeURIComponent(data));
    } catch {
      return data;
    }
  }
};

const decrypt = (cipher: string) => {
  try {
    if (!SECRET_KEY) {
      try {
        return decodeURIComponent(atob(cipher));
      } catch {
        return null;
      }
    }
    const bytes = CryptoJS.AES.decrypt(cipher, SECRET_KEY);
    const result = bytes.toString(CryptoJS.enc.Utf8);
    if (!result) {
      try {
        return decodeURIComponent(atob(cipher));
      } catch {
        return null;
      }
    }
    return result || null;
  } catch (err) {
    console.error("Decrypt error:", err);
    try {
      return decodeURIComponent(atob(cipher));
    } catch {
      return null;
    }
  }
};

const parseStoredSession = (decrypted: string): StoredAuthSession | null => {
  try {
    const parsed = JSON.parse(decrypted) as StoredAuthSession;
    if (parsed?.id && (parsed.type === "user" || parsed.type === "admin")) {
      return parsed;
    }
  } catch {
    // Legacy cookie stored only the user id string
    if (decrypted) {
      return { id: decrypted, type: "user" };
    }
  }
  return null;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | AuthAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkCookieAuth = () => {
      try {
        const encryptedCookie = Cookies.get("authId");

        if (encryptedCookie) {
          const decrypted = decrypt(encryptedCookie);
          const session = decrypted ? parseStoredSession(decrypted) : null;

          if (session) {
            if (session.type === "admin" && session.sessionToken) {
              setUser({
                id: session.id,
                type: "admin",
                sessionToken: session.sessionToken,
                email: "",
                created_at: "",
                name: "Admin",
              });
            } else {
              setUser({
                id: session.id,
                type: "user",
              } as AuthUser);
            }
          }
        }
      } catch (error) {
        console.error("Error restoring auth session:", error);
        Cookies.remove("authId");
      } finally {
        setIsLoading(false);
      }
    };

    checkCookieAuth();
  }, []);

  const login = (userData: AuthUser | AuthAdmin) => {
    setUser(userData);

    const payload: StoredAuthSession = {
      id: userData.id,
      type: userData.type,
      ...(userData.type === "admin"
        ? { sessionToken: userData.sessionToken }
        : {}),
    };

    Cookies.set("authId", encrypt(JSON.stringify(payload)), {
      expires: 7,
      secure: true,
      sameSite: "Strict",
    });

    if (userData.type === "admin") navigate("/admin-dashboard");
    else navigate("/user-dashboard");
  };

  const logout = () => {
    setUser(null);
    Cookies.remove("authId");
    navigate("/");
  };

  const adminSessionToken =
    user?.type === "admin" ? user.sessionToken : null;

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading,
    adminSessionToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
